/**
 * TestRunner — Automated UI replay engine for Vertical Solitaire.
 *
 * Replays a sequence of high-level actions (tapStock, drag, doubleTap, tapUndo,
 * tapAutoComplete, assert, waitForWin) by resolving card/button positions at
 * runtime and firing real mock touch events through the existing canvas handlers.
 *
 * This exercises the exact same code paths as a human player — no shortcuts.
 *
 * Triggered via URL params: ?test=draw1 or ?test=draw3
 */

import {
    canvasTouchStart,
    canvasTouchMove,
    canvasTouchEnd,
    canvasTouchDoubleTap,
    resetDoubleTapState,
} from './canvasMouseMethods'
import {
    appState,
    getCurrentCardState,
    checkForWin,
    getDraggedCards,
    setCardState,
    clearUndoHistory,
    setAppState,
} from './state'
import { CurrentAppState } from '../Models/AppState'
import { disableCardIntegrity } from './cardIntegrity'
import { assignCardCoordinates } from './assignCardCoordinates'
import { getCardPosition } from './cardPositions'
import { animationManager } from './animations'
import { canAutoComplete } from './autocompleteGame'
import { schedulePaint } from './paint'
import {
    stockCardX,
    stockCardY,
    undoButtonX,
    undoButtonY,
    autoCompleteButtonX,
    autoCompleteButtonY,
    menuButtonX,
    menuButtonY,
    newGameButtonX,
    newGameButtonY,
    tableauCardX,
    tableauCardY,
    tableauCardStep,
    foundationCardX,
    foundationCardY,
    isLeftHanded,
    isLandscapeLayout,
} from './allXYCoordinates'
import { isCardOverFoundation, isCardOverTableau } from './findCardIndex'
import { Card } from '../Models/Card'
import type { TestAction } from './testSolver'
import { executeMenuAction } from './menuActions'

// ──────────────────────────────────────────────────────────────────────────────
// Mock touch event creation
// ──────────────────────────────────────────────────────────────────────────────

function createMockTouch(x: number, y: number, type: string) {
    return {
        changedTouches: [
            {
                clientX: x,
                clientY: y,
                pageX: x,
                pageY: y,
                screenX: x,
                screenY: y,
            },
        ],
        preventDefault: () => {},
        type,
        touches: [{ clientX: x, clientY: y }],
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// Card lookup helpers
// ──────────────────────────────────────────────────────────────────────────────

/** Parse a card ID like "AH", "10D", "KS" into { value, type }. */
function parseCardId(id: string): { value: string; type: string } {
    // Last character is suit type; everything before is the value
    const type = id.slice(-1) // 'H', 'D', 'S', 'C'
    const value = id.slice(0, -1) // 'A', '2'..'10', 'J', 'Q', 'K'
    return { value, type }
}

/** Convert a card value string ('A','2'..'10','J','Q','K') to numeric (1..13). */
function valueToNum(value: string): number {
    const map: Record<string, number> = { A: 1, J: 11, Q: 12, K: 13 }
    return map[value] ?? parseInt(value, 10)
}

/** Find a card object in the current game state by its ID (e.g. "8H"). */
function findCard(cardId: string): { card: Card; location: string } | null {
    const { value, type } = parseCardId(cardId)
    const state = getCurrentCardState()

    // Search tableau
    for (let i = 0; i < state.tableau.length; i++) {
        for (let j = 0; j < state.tableau[i].length; j++) {
            const card = state.tableau[i][j]
            if (card.value === value && card.type === type) {
                return { card, location: `tableau[${i}][${j}]` }
            }
        }
    }

    // Search waste (top card only for interaction, but search all for finding)
    for (let i = state.waste.length - 1; i >= 0; i--) {
        const card = state.waste[i]
        if (card.value === value && card.type === type) {
            return { card, location: `waste[${i}]` }
        }
    }

    // Search foundation
    for (let i = 0; i < state.foundation.length; i++) {
        for (let j = state.foundation[i].length - 1; j >= 0; j--) {
            const card = state.foundation[i][j]
            if (card.value === value && card.type === type) {
                return { card, location: `foundation[${i}][${j}]` }
            }
        }
    }

    // Search stock
    for (let i = state.stock.length - 1; i >= 0; i--) {
        const card = state.stock[i]
        if (card.value === value && card.type === type) {
            return { card, location: `stock[${i}]` }
        }
    }

    return null
}

/**
 * Get a pickup point that uniquely targets a specific card, even when it's partially
 * covered by overlapping cards in a tableau cascade.
 *
 * For tableau cards that aren't the top card in their pile, the center may be
 * covered by the next card (which `findCardInTableau` would return instead, since
 * it iterates backward). This function targets the visible (uncovered) sliver.
 *
 * - Portrait: cascades horizontally; handedness decides which edge is visible.
 * - Landscape: cascades vertically; visible sliver is at the top of the card.
 *
 * For non-tableau cards (waste, foundation, stock) or the top card in a tableau
 * pile, the regular center is returned.
 */
function getCardPickupPoint(card: Card, location: string): { x: number; y: number } {
    const pos = getCardPosition(card)
    const centerY = pos.y + appState.cardHeight / 2

    // Check if this card is in a tableau pile and not the top card
    const tableauMatch = location.match(/^tableau\[(\d+)\]\[(\d+)\]$/)
    if (tableauMatch) {
        const pileIndex = parseInt(tableauMatch[1], 10)
        const cardIndex = parseInt(tableauMatch[2], 10)
        const state = getCurrentCardState()
        const pile = state.tableau[pileIndex]

        if (cardIndex < pile.length - 1) {
            // This card is covered by the next card in the cascade.
            // Target the visible sliver instead of the center.
            const step = tableauCardStep(card.isFaceDown)

            let targetX: number
            let targetY: number

            if (isLandscapeLayout()) {
                targetX = pos.x + appState.cardWidth / 2
                targetY = pos.y + step / 2
            } else if (isLeftHanded()) {
                // Left-handed: cascade goes RIGHT, visible sliver is on the LEFT edge
                targetX = pos.x + step / 2
                targetY = centerY
            } else {
                // Right-handed: cascade goes LEFT, visible sliver is on the RIGHT edge
                targetX = pos.x + appState.cardWidth - step / 2
                targetY = centerY
            }

            console.log(
                `[TestRunner]   pickup targeting visible sliver: card at index ${cardIndex}/${pile.length - 1} in tableau[${pileIndex}], ` +
                    `step=${step.toFixed(1)}, targeting (${Math.round(targetX)}, ${Math.round(targetY)}) instead of center (${Math.round(pos.x + appState.cardWidth / 2)}, ${Math.round(centerY)})`
            )

            return { x: targetX, y: targetY }
        }
    }

    // Default: center of card (top card in tableau, or waste/foundation/stock)
    return {
        x: pos.x + appState.cardWidth / 2,
        y: centerY,
    }
}

/** Get the center of the stock pile area. */
function getStockCenter(): { x: number; y: number } {
    return {
        x: stockCardX() + appState.cardWidth / 2,
        y: stockCardY() + appState.cardHeight / 2,
    }
}

/** Get the center of the undo button. */
function getUndoCenter(): { x: number; y: number } {
    return {
        x: undoButtonX() + appState.cardWidth / 2,
        y: undoButtonY() + appState.cardWidth / 2, // Buttons are square: cardWidth × cardWidth
    }
}

/** Get the center of the auto-complete button. */
function getAutoCompleteCenter(): { x: number; y: number } {
    return {
        x: autoCompleteButtonX() + appState.cardWidth / 2,
        y: autoCompleteButtonY() + appState.cardWidth / 2,
    }
}

/** Get the center of the menu button. */
function getMenuButtonCenter(): { x: number; y: number } {
    return {
        x: menuButtonX() + appState.cardWidth / 2,
        y: menuButtonY() + appState.cardWidth / 2,
    }
}

/** Get the center of the new game button. */
function getNewGameButtonCenter(): { x: number; y: number } {
    return {
        x: newGameButtonX() + appState.cardWidth / 2,
        y: newGameButtonY() + appState.cardWidth / 2,
    }
}

/**
 * Get the drop target center for a tableau pile.
 * If the pile has cards, target just past the last card's horizontal cascade.
 * If empty, target the center of the placeholder.
 */
function getTableauDropTarget(tableauIndex: number): { x: number; y: number } {
    const state = getCurrentCardState()
    const pile = state.tableau[tableauIndex]
    const baseX = tableauCardX(tableauIndex)
    const baseY = tableauCardY(tableauIndex)

    if (pile.length === 0) {
        // Empty pile — center of placeholder
        return {
            x: baseX + appState.cardWidth / 2,
            y: baseY + appState.cardHeight / 2,
        }
    }

    // Target the center of the top (last) card in the pile
    const topCard = pile[pile.length - 1]
    const topPos = getCardPosition(topCard)
    return {
        x: topPos.x + appState.cardWidth / 2,
        y: topPos.y + appState.cardHeight / 2,
    }
}

/**
 * Get the drop target center for a foundation pile.
 */
function getFoundationDropTarget(foundationIndex: number): { x: number; y: number } {
    return {
        x: foundationCardX(foundationIndex) + appState.cardWidth / 2,
        y: foundationCardY(foundationIndex) + appState.cardHeight / 2,
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// Wait helpers
// ──────────────────────────────────────────────────────────────────────────────

/** Wait for a fixed number of milliseconds. */
function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Wait until all animations have completed, with a timeout. */
async function waitForAnimations(timeoutMs: number = 5000): Promise<void> {
    const startTime = Date.now()
    while (animationManager.isAnimating()) {
        if (Date.now() - startTime > timeoutMs) {
            console.warn('[TestRunner] Animation timeout — forcing clear')
            animationManager.clearAll()
            return
        }
        await delay(16) // ~1 frame
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// Face-down card auto-flip
// ──────────────────────────────────────────────────────────────────────────────

// Auto-flip of exposed face-down cards is now handled by commitMove() in state.ts.
// The flipAnyExposedFaceDownCards() helper that used to live here has been removed.

// ──────────────────────────────────────────────────────────────────────────────
// Action executors
// ──────────────────────────────────────────────────────────────────────────────

async function executeTapStock(): Promise<void> {
    resetDoubleTapState()

    // Snapshot before
    const before = getCurrentCardState()
    const beforeStockLen = before.stock.length
    const beforeWasteLen = before.waste.length
    const beforeWasteTop =
        beforeWasteLen > 0
            ? `${before.waste[beforeWasteLen - 1].value}${before.waste[beforeWasteLen - 1].type}`
            : '(empty)'

    const center = getStockCenter()
    const touchEvent = createMockTouch(center.x, center.y, 'touchstart')
    // @ts-ignore — mock touch event
    canvasTouchStart(touchEvent)

    await delay(50)

    const endEvent = createMockTouch(center.x, center.y, 'touchend')
    // @ts-ignore
    canvasTouchEnd(endEvent)

    await waitForAnimations()
    await delay(50)

    // Validate the draw happened
    const after = getCurrentCardState()
    const afterStockLen = after.stock.length
    const afterWasteLen = after.waste.length
    const afterWasteTop =
        afterWasteLen > 0
            ? `${after.waste[afterWasteLen - 1].value}${after.waste[afterWasteLen - 1].type}`
            : '(empty)'

    if (beforeStockLen > 0) {
        // Should have drawn cards: stock decreases, waste increases
        const numDrawn = appState.NUM_CARDS_FLIPPED
        const expectedDraw = Math.min(numDrawn, beforeStockLen)
        if (afterStockLen !== beforeStockLen - expectedDraw) {
            throw new Error(
                `tapStock VALIDATION FAILED: Expected stock ${beforeStockLen} → ${beforeStockLen - expectedDraw}, got ${afterStockLen}. ` +
                    `Waste: ${beforeWasteLen} → ${afterWasteLen}. Waste top: ${beforeWasteTop} → ${afterWasteTop}`
            )
        }
    } else {
        // Stock was empty — should have reset waste to stock
        if (afterWasteLen !== 0) {
            throw new Error(
                `tapStock VALIDATION FAILED (reset): Expected waste to be empty after reset, got ${afterWasteLen}. ` +
                    `Stock: ${beforeStockLen} → ${afterStockLen}`
            )
        }
    }
}

async function executeDrag(cardId: string, target: string): Promise<void> {
    resetDoubleTapState()
    const found = findCard(cardId)
    if (!found) {
        throw new Error(`Card ${cardId} not found in current state`)
    }

    // Check if card is face-down (can't drag face-down cards)
    if (found.card.isFaceDown) {
        throw new Error(`Card ${cardId} is face-down at ${found.location} — cannot drag`)
    }

    const sourceLocation = found.location
    const cardCenter = getCardPickupPoint(found.card, found.location)
    let dropTarget: { x: number; y: number }
    let targetPileType: 'tableau' | 'foundation'
    let targetIndex: number
    if (target.startsWith('tableau')) {
        targetPileType = 'tableau'
        targetIndex = parseInt(target.replace('tableau', ''), 10)
        dropTarget = getTableauDropTarget(targetIndex)
    } else if (target.startsWith('foundation')) {
        targetPileType = 'foundation'
        // The solver assigns foundation indices based on its own tracking, but
        // doubleTap moves may have placed cards on different foundations than the
        // solver expected.  Dynamically find the correct foundation pile that can
        // accept this card based on current game state.
        const { value, type: cardType } = parseCardId(cardId)
        const currentState = getCurrentCardState()
        const solverIndex = parseInt(target.replace('foundation', ''), 10)
        let resolvedIndex = solverIndex
        // Check if the solver's target foundation can actually accept this card
        const solverPile = currentState.foundation[solverIndex]
        const cardNumValue = valueToNum(value)
        const canUseSolverPile =
            (solverPile.length === 0 && cardNumValue === 1) ||
            (solverPile.length > 0 &&
                solverPile[solverPile.length - 1].type === cardType &&
                solverPile[solverPile.length - 1].numValue === cardNumValue - 1)
        if (!canUseSolverPile) {
            // Find the actual foundation that can accept this card
            for (let fi = 0; fi < currentState.foundation.length; fi++) {
                const pile = currentState.foundation[fi]
                const canPlace =
                    (pile.length === 0 && cardNumValue === 1) ||
                    (pile.length > 0 &&
                        pile[pile.length - 1].type === cardType &&
                        pile[pile.length - 1].numValue === cardNumValue - 1)
                if (canPlace) {
                    resolvedIndex = fi
                    console.log(
                        `[TestRunner]   foundation redirect: solver wanted ${target} but using foundation${fi} (suit mismatch)`
                    )
                    break
                }
            }
        }
        targetIndex = resolvedIndex
        dropTarget = getFoundationDropTarget(targetIndex)
    } else {
        throw new Error(`Unknown drag target: ${target}`)
    }

    // 1. Touch start on the card
    console.log(
        `[TestRunner]   drag debug: pickup at (${Math.round(cardCenter.x)}, ${Math.round(cardCenter.y)}), drop at (${Math.round(dropTarget.x)}, ${Math.round(dropTarget.y)})`
    )
    const startEvent = createMockTouch(cardCenter.x, cardCenter.y, 'touchstart')
    // @ts-ignore
    canvasTouchStart(startEvent)

    // Verify card was picked up (should be in draggedCards)
    const draggedAfterPickup = getDraggedCards()
    console.log(`[TestRunner]   drag debug: after pickup, draggedCards=${draggedAfterPickup.length}`)
    if (draggedAfterPickup.length === 0) {
        throw new Error(
            `drag PICKUP FAILED: ${cardId} was not picked up. ` +
                `Source: ${sourceLocation}. Card position: (${Math.round(found.card.x)}, ${Math.round(found.card.y)}). ` +
                `Touch: (${Math.round(cardCenter.x)}, ${Math.round(cardCenter.y)})`
        )
    }

    await delay(30)

    // 2. Touch move — interpolate a few steps for realism
    const steps = 5
    for (let i = 1; i <= steps; i++) {
        const t = i / steps
        const moveX = cardCenter.x + (dropTarget.x - cardCenter.x) * t
        const moveY = cardCenter.y + (dropTarget.y - cardCenter.y) * t
        const moveEvent = createMockTouch(moveX, moveY, 'touchmove')
        // @ts-ignore
        canvasTouchMove(moveEvent)
        await delay(16)
    }

    // 3. Touch end at the drop target
    const endEvent = createMockTouch(dropTarget.x, dropTarget.y, 'touchend')
    // @ts-ignore
    canvasTouchEnd(endEvent)

    await waitForAnimations()
    await delay(50)

    // Validate the card arrived at the target pile
    const { value, type } = parseCardId(cardId)
    const afterState = getCurrentCardState()
    const targetPile =
        targetPileType === 'tableau' ? afterState.tableau[targetIndex] : afterState.foundation[targetIndex]
    const cardInTarget = targetPile.some((c) => c.value === value && c.type === type)
    if (!cardInTarget) {
        // Card didn't arrive — check where it ended up
        const afterFound = findCard(cardId)
        const afterLocation = afterFound ? afterFound.location : 'NOT FOUND'
        // Debug: log the drop coordinates and hit detection results
        const dbgIsOverTableau = isCardOverTableau(dropTarget.x, dropTarget.y)
        const dbgIsOverFoundation = isCardOverFoundation(dropTarget.x, dropTarget.y)
        throw new Error(
            `drag VALIDATION FAILED: ${cardId} was NOT placed on ${target}. ` +
                `Source was: ${sourceLocation}. Now at: ${afterLocation}. ` +
                `Drop coords: (${Math.round(dropTarget.x)}, ${Math.round(dropTarget.y)}). ` +
                `isOverTableau=${dbgIsOverTableau}, isOverFoundation=${dbgIsOverFoundation}. ` +
                `Card pickup at: (${Math.round(cardCenter.x)}, ${Math.round(cardCenter.y)}). ` +
                `Target pile has ${targetPile.length} cards: [${targetPile.map((c) => c.value + c.type).join(', ')}]`
        )
    }
}

async function executeDoubleTap(cardId: string): Promise<void> {
    const found = findCard(cardId)
    if (!found) {
        throw new Error(`Card ${cardId} not found in current state`)
    }

    const sourceLocation = found.location
    const cardCenter = getCardPickupPoint(found.card, found.location)
    const event = createMockTouch(cardCenter.x, cardCenter.y, 'touchend')
    // @ts-ignore
    canvasTouchDoubleTap(event)

    await waitForAnimations()
    await delay(50)

    // Validate the card moved to a foundation
    const { value, type } = parseCardId(cardId)
    const afterState = getCurrentCardState()
    let foundInFoundation = false
    for (let fi = 0; fi < afterState.foundation.length; fi++) {
        if (afterState.foundation[fi].some((c) => c.value === value && c.type === type)) {
            foundInFoundation = true
            break
        }
    }
    if (!foundInFoundation) {
        const afterFound = findCard(cardId)
        const afterLocation = afterFound ? afterFound.location : 'NOT FOUND'
        throw new Error(
            `doubleTap VALIDATION FAILED: ${cardId} was NOT moved to foundation. ` +
                `Source was: ${sourceLocation}. Now at: ${afterLocation}`
        )
    }
}

async function executeTapUndo(): Promise<void> {
    resetDoubleTapState()
    const center = getUndoCenter()
    const touchEvent = createMockTouch(center.x, center.y, 'touchstart')
    // @ts-ignore
    canvasTouchStart(touchEvent)

    await delay(50)

    const endEvent = createMockTouch(center.x, center.y, 'touchend')
    // @ts-ignore
    canvasTouchEnd(endEvent)

    await waitForAnimations()
    await delay(50)
}

async function executeTapAutoComplete(): Promise<void> {
    resetDoubleTapState()

    // If the game is already won, skip auto-complete gracefully
    const preState = getCurrentCardState()
    const preFoundationTotal = preState.foundation.reduce((s, p) => s + p.length, 0)
    if (preFoundationTotal === 52) {
        console.log('[TestRunner]   tapAutoComplete: game already won (52 in foundations) — skipping')
        return
    }

    // First verify auto-complete is available
    if (!canAutoComplete()) {
        // Diagnose why auto-complete isn't available
        const state = getCurrentCardState()
        const diag: string[] = []
        if (state.stock.length > 0) diag.push(`stock has ${state.stock.length} cards`)
        if (state.waste.length > 0) diag.push(`waste has ${state.waste.length} cards`)
        for (let i = 0; i < state.tableau.length; i++) {
            const faceDown = state.tableau[i].filter((c) => c.isFaceDown)
            if (faceDown.length > 0) diag.push(`tableau[${i}] has ${faceDown.length} face-down`)
        }
        const fTotal = state.foundation.reduce((s, p) => s + p.length, 0)
        if (fTotal === 52) diag.push('already won (foundation=52)')
        throw new Error(`Auto-complete is not available — conditions not met: ${diag.join('; ')}`)
    }

    const center = getAutoCompleteCenter()
    const touchEvent = createMockTouch(center.x, center.y, 'touchstart')
    // @ts-ignore
    canvasTouchStart(touchEvent)

    await delay(50)

    const endEvent = createMockTouch(center.x, center.y, 'touchend')
    // @ts-ignore
    canvasTouchEnd(endEvent)

    // Auto-complete chains many animations with 50ms delays between them.
    // Wait generously for all of them to finish.
    await waitForAutoComplete(30000)
    await delay(200)
}

/** Wait for auto-complete to finish (all foundations at 13). */
async function waitForAutoComplete(timeoutMs: number = 30000): Promise<void> {
    const startTime = Date.now()
    while (Date.now() - startTime < timeoutMs) {
        const state = getCurrentCardState()
        const totalFoundation = state.foundation.reduce((sum, pile) => sum + pile.length, 0)
        if (totalFoundation === 52) return
        await delay(100)
    }
    console.warn('[TestRunner] Auto-complete timeout')
}

async function executeAssert(condition: string, description: string): Promise<boolean> {
    const state = getCurrentCardState()
    try {
        // Evaluate condition in context with state and appState available.
        // SECURITY NOTE: `new Function()` is used intentionally here to evaluate
        // dynamic assertion strings from test scenario definitions (which are
        // developer-authored, not user-supplied). This code only runs in the
        // in-app test runner, never in normal gameplay. The CSP includes
        // 'unsafe-eval' to permit this usage.
        const result = new Function('state', 'appState', `return (${condition})`)(state, appState)
        if (!result) {
            console.error(`[TestRunner] ASSERT FAILED: ${description} (condition: ${condition})`)
            return false
        }
        return true
    } catch (err) {
        console.error(`[TestRunner] ASSERT ERROR: ${description} — ${err}`)
        return false
    }
}

async function executeWaitForWin(): Promise<boolean> {
    // Check immediately first
    if (checkForWin()) return true

    // Wait a bit in case auto-complete is still finishing
    for (let i = 0; i < 100; i++) {
        await delay(100)
        if (checkForWin()) return true
    }

    console.error('[TestRunner] WIN CHECK FAILED — game not won after waiting')
    return false
}

// ──────────────────────────────────────────────────────────────────────────────
// Direct state seeding (for reliable screenshot scenarios)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Build a complete won-game state: all 52 cards in 4 foundation piles (A→K),
 * all other piles empty. Then transition to Completed app state.
 *
 * This bypasses the full game replay, making win-screen screenshots 100% reliable.
 */
async function executeSeedWinState(): Promise<void> {
    const suits: Array<{ type: string; color: string }> = [
        { type: 'S', color: 'black' },
        { type: 'C', color: 'black' },
        { type: 'D', color: 'red' },
        { type: 'H', color: 'red' },
    ]

    const values: Array<{ value: string; numValue: number }> = [
        { value: 'A', numValue: 1 },
        { value: '2', numValue: 2 },
        { value: '3', numValue: 3 },
        { value: '4', numValue: 4 },
        { value: '5', numValue: 5 },
        { value: '6', numValue: 6 },
        { value: '7', numValue: 7 },
        { value: '8', numValue: 8 },
        { value: '9', numValue: 9 },
        { value: '10', numValue: 10 },
        { value: 'J', numValue: 11 },
        { value: 'Q', numValue: 12 },
        { value: 'K', numValue: 13 },
    ]

    // Build 4 foundation piles: Ace through King for each suit
    const foundation: [Card[], Card[], Card[], Card[]] = [[], [], [], []]
    for (let si = 0; si < suits.length; si++) {
        for (const v of values) {
            foundation[si].push({
                type: suits[si].type,
                color: suits[si].color,
                value: v.value,
                numValue: v.numValue,
                isFaceDown: false,
                x: 0,
                y: 0,
            } as Card)
        }
    }

    const winState = {
        foundation,
        stock: [] as Card[],
        waste: [] as Card[],
        tableau: [[], [], [], [], [], [], []] as [Card[], Card[], Card[], Card[], Card[], Card[], Card[]],
        draggedCards: [] as Card[],
        wasteVisibleCount: 0,
    }

    // Disable integrity check since we're seeding state directly, then commit
    disableCardIntegrity()
    const withCoords = assignCardCoordinates(winState)
    setCardState(withCoords, { skipIntegrity: true })

    // Transition to Completed state (triggers win animation on next paint)
    clearUndoHistory()
    setAppState({ ...appState, currentAppState: CurrentAppState.Completed })

    // Allow a paint cycle so the win animation starts
    schedulePaint()
    await delay(200)

    console.log('[TestRunner] seedWinState: state seeded, app state set to Completed')
}

// ──────────────────────────────────────────────────────────────────────────────
// UI / menu interaction executors
// ──────────────────────────────────────────────────────────────────────────────

async function executeTapMenuButton(): Promise<void> {
    resetDoubleTapState()
    const center = getMenuButtonCenter()
    const touchEvent = createMockTouch(center.x, center.y, 'touchstart')
    // @ts-ignore — mock touch event
    canvasTouchStart(touchEvent)

    await delay(50)

    const endEvent = createMockTouch(center.x, center.y, 'touchend')
    // @ts-ignore
    canvasTouchEnd(endEvent)

    await waitForAnimations()
    await delay(100) // extra delay for DOM menu to show/hide
}

async function executeTapNewGameButton(): Promise<void> {
    resetDoubleTapState()
    const center = getNewGameButtonCenter()

    // Snapshot state before (to verify a new game actually started)
    const beforeState = getCurrentCardState()
    const beforeStockLen = beforeState.stock.length

    const touchEvent = createMockTouch(center.x, center.y, 'touchstart')
    // @ts-ignore
    canvasTouchStart(touchEvent)

    await delay(50)

    const endEvent = createMockTouch(center.x, center.y, 'touchend')
    // @ts-ignore
    canvasTouchEnd(endEvent)

    await waitForAnimations()
    // startNewGame() is async (loads images), wait for it to settle
    await delay(500)
}

async function executeTapMenuHitArea(hitAreaId: string): Promise<void> {
    // Ensure a paint has run so hit areas are populated
    schedulePaint()
    await delay(100)

    // Execute the menu action directly (same as what canvasMouseMethods does)
    const handled = executeMenuAction(hitAreaId)
    if (!handled) {
        throw new Error(
            `tapMenuHitArea: executeMenuAction returned false for '${hitAreaId}'. ` +
                `Is the menu open? appState.currentAppState = ${appState.currentAppState}`
        )
    }

    // Theme changes and new-game are async (image reload) — wait generously
    if (hitAreaId.startsWith('theme-') || hitAreaId === 'new-game') {
        await delay(500)
    }

    schedulePaint()
    await waitForAnimations()
    await delay(100)
}

function executeAssertAppState(expected: 'Playing' | 'Menu' | 'Completed', description: string): boolean {
    const stateMap: Record<string, CurrentAppState> = {
        Playing: CurrentAppState.Playing,
        Menu: CurrentAppState.Menu,
        Completed: CurrentAppState.Completed,
    }
    const expectedValue = stateMap[expected]
    const actual = appState.currentAppState

    if (actual !== expectedValue) {
        const reverseMap = ['Playing', 'Menu', 'Completed']
        console.error(
            `[TestRunner] ASSERT APP STATE FAILED: ${description}. ` +
                `Expected ${expected} (${expectedValue}), got ${reverseMap[actual]} (${actual})`
        )
        return false
    }
    return true
}

function executeAssertSetting(key: string, expected: unknown, description: string): boolean {
    const actual = (appState.settings as unknown as Record<string, unknown>)[key]
    if (actual !== expected) {
        console.error(
            `[TestRunner] ASSERT SETTING FAILED: ${description}. ` +
                `Expected settings.${key} = ${JSON.stringify(expected)}, got ${JSON.stringify(actual)}`
        )
        return false
    }
    return true
}

// ──────────────────────────────────────────────────────────────────────────────
// Visual status overlay (visible on screen for simulator/device testing)
// ──────────────────────────────────────────────────────────────────────────────

let statusOverlay: HTMLElement | null = null

function isOverlaySuppressedForScreenshots(): boolean {
    const nativeScreenshotMode = (window as any).__nativeScreenshotMode === true
    if (nativeScreenshotMode) return true

    const params = new URLSearchParams(window.location.search)
    const screenshotParam = params.get('screenshot')
    const overlayParam = params.get('overlay')

    return screenshotParam === '1' || overlayParam === '0' || overlayParam === 'off'
}

function createStatusOverlay(): void {
    if (isOverlaySuppressedForScreenshots()) return

    statusOverlay = document.createElement('div')
    statusOverlay.id = 'test-runner-status'
    statusOverlay.style.cssText = [
        'position: fixed',
        'top: 40px',
        'left: 8px',
        'right: 8px',
        'padding: 8px 12px',
        'background: rgba(0, 0, 0, 0.85)',
        'color: #00ff88',
        'font-family: monospace',
        'font-size: 9px',
        'line-height: 1.4',
        'border-radius: 8px',
        'z-index: 99999',
        'pointer-events: none',
        'white-space: pre-wrap',
        'max-height: 60px',
        'overflow: hidden',
    ].join('; ')
    document.body.appendChild(statusOverlay)
}

function updateStatus(text: string, color: string = '#00ff88'): void {
    if (isOverlaySuppressedForScreenshots()) return

    if (!statusOverlay) createStatusOverlay()
    statusOverlay!.style.color = color
    statusOverlay!.textContent = text

    // On failure (red), expand overlay to show full error + logs, and make it scrollable
    if (color === '#ff4444') {
        statusOverlay!.style.maxHeight = '80vh'
        statusOverlay!.style.overflow = 'auto'
        statusOverlay!.style.pointerEvents = 'auto'
    }
}

function removeStatusOverlay(): void {
    if (statusOverlay) {
        statusOverlay.remove()
        statusOverlay = null
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// Debug log capture — captures console.log output during test for display
// ──────────────────────────────────────────────────────────────────────────────

let debugLogBuffer: string[] = []
let originalConsoleLog: typeof console.log | null = null

function startCapturingLogs(): void {
    debugLogBuffer = []
    originalConsoleLog = console.log
    console.log = (...args: unknown[]) => {
        const msg = args.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ')
        debugLogBuffer.push(msg)
        originalConsoleLog!(...args)
    }
}

function stopCapturingLogs(): void {
    if (originalConsoleLog) {
        console.log = originalConsoleLog
        originalConsoleLog = null
    }
}

function getRecentLogs(count: number = 20): string {
    return debugLogBuffer.slice(-count).join('\n')
}

// ──────────────────────────────────────────────────────────────────────────────
// Main test runner
// ──────────────────────────────────────────────────────────────────────────────

interface TestResult {
    passed: boolean
    totalActions: number
    completedActions: number
    failedAction?: { index: number; action: TestAction; error: string }
    duration: number
}

/**
 * Run a sequence of test actions against the live game.
 * The game should already be dealt with the correct seed before calling this.
 */
export async function runTestActions(actions: TestAction[], testName: string): Promise<TestResult> {
    const startTime = Date.now()
    console.log(`\n[TestRunner] ═══════════════════════════════════════════`)
    console.log(`[TestRunner] Starting: ${testName}`)
    console.log(`[TestRunner] Actions: ${actions.length}`)
    console.log(`[TestRunner] ═══════════════════════════════════════════\n`)

    updateStatus(`TEST: ${testName}\n0/${actions.length} actions...`)

    startCapturingLogs()
    let completedActions = 0

    for (let i = 0; i < actions.length; i++) {
        const action = actions[i]
        const actionDesc = formatAction(action)
        const progressPct = Math.floor(((i + 1) / actions.length) * 100)

        try {
            switch (action.type) {
                case 'tapStock':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} — tapStock`)
                    updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: tapStock`)
                    await executeTapStock()
                    break

                case 'drag':
                    console.log(
                        `[TestRunner] ${i + 1}/${actions.length} — drag ${action.cardId} → ${action.target}`
                    )
                    updateStatus(
                        `${testName} [${progressPct}%]\n${i + 1}/${actions.length}: drag ${action.cardId} → ${action.target}`
                    )
                    await executeDrag(action.cardId, action.target)
                    break

                case 'doubleTap':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} — doubleTap ${action.cardId}`)
                    updateStatus(
                        `${testName} [${progressPct}%]\n${i + 1}/${actions.length}: doubleTap ${action.cardId}`
                    )
                    await executeDoubleTap(action.cardId)
                    break

                case 'tapUndo':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} — tapUndo`)
                    updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: tapUndo`)
                    await executeTapUndo()
                    break

                case 'tapAutoComplete':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} — tapAutoComplete`)
                    updateStatus(
                        `${testName} [${progressPct}%]\n${i + 1}/${actions.length}: tapAutoComplete...`
                    )
                    await executeTapAutoComplete()
                    break

                case 'assert': {
                    console.log(`[TestRunner] ${i + 1}/${actions.length} — assert: ${action.description}`)
                    updateStatus(
                        `${testName} [${progressPct}%]\n${i + 1}/${actions.length}: assert ${action.description}`
                    )
                    const assertPassed = await executeAssert(action.condition, action.description)
                    if (!assertPassed) {
                        const result: TestResult = {
                            passed: false,
                            totalActions: actions.length,
                            completedActions,
                            failedAction: {
                                index: i,
                                action,
                                error: `Assertion failed: ${action.description}`,
                            },
                            duration: Date.now() - startTime,
                        }
                        updateStatus(
                            `FAILED at ${i + 1}/${actions.length}\nassert: ${action.description}`,
                            '#ff4444'
                        )
                        return result
                    }
                    break
                }

                case 'waitForWin': {
                    console.log(`[TestRunner] ${i + 1}/${actions.length} — waitForWin`)
                    updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: waitForWin...`)
                    const won = await executeWaitForWin()
                    if (!won) {
                        const result: TestResult = {
                            passed: false,
                            totalActions: actions.length,
                            completedActions,
                            failedAction: {
                                index: i,
                                action,
                                error: 'Game not won',
                            },
                            duration: Date.now() - startTime,
                        }
                        updateStatus(`FAILED at ${i + 1}/${actions.length}\nGame not won!`, '#ff4444')
                        return result
                    }
                    break
                }

                case 'seedWinState': {
                    console.log(`[TestRunner] ${i + 1}/${actions.length} — seedWinState`)
                    updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: seedWinState...`)
                    await executeSeedWinState()
                    break
                }

                // ── UI / menu interaction actions ──

                case 'tapMenuButton':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} — tapMenuButton`)
                    updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: tapMenuButton`)
                    await executeTapMenuButton()
                    break

                case 'tapNewGameButton':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} — tapNewGameButton`)
                    updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: tapNewGameButton`)
                    await executeTapNewGameButton()
                    break

                case 'tapMenuHitArea':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} — tapMenuHitArea ${action.hitAreaId}`)
                    updateStatus(
                        `${testName} [${progressPct}%]\n${i + 1}/${actions.length}: tap ${action.hitAreaId}`
                    )
                    await executeTapMenuHitArea(action.hitAreaId)
                    break

                case 'assertAppState': {
                    console.log(
                        `[TestRunner] ${i + 1}/${actions.length} — assertAppState: ${action.description}`
                    )
                    updateStatus(
                        `${testName} [${progressPct}%]\n${i + 1}/${actions.length}: assertAppState ${action.expected}`
                    )
                    const appStatePassed = executeAssertAppState(action.expected, action.description)
                    if (!appStatePassed) {
                        const result: TestResult = {
                            passed: false,
                            totalActions: actions.length,
                            completedActions,
                            failedAction: {
                                index: i,
                                action,
                                error: `App state assertion failed: ${action.description}`,
                            },
                            duration: Date.now() - startTime,
                        }
                        updateStatus(
                            `FAILED at ${i + 1}/${actions.length}\nassertAppState: ${action.description}`,
                            '#ff4444'
                        )
                        return result
                    }
                    break
                }

                case 'assertSetting': {
                    console.log(
                        `[TestRunner] ${i + 1}/${actions.length} — assertSetting: ${action.description}`
                    )
                    updateStatus(
                        `${testName} [${progressPct}%]\n${i + 1}/${actions.length}: assertSetting ${action.key}=${JSON.stringify(action.expected)}`
                    )
                    const settingPassed = executeAssertSetting(action.key, action.expected, action.description)
                    if (!settingPassed) {
                        const result: TestResult = {
                            passed: false,
                            totalActions: actions.length,
                            completedActions,
                            failedAction: {
                                index: i,
                                action,
                                error: `Setting assertion failed: ${action.description}`,
                            },
                            duration: Date.now() - startTime,
                        }
                        updateStatus(
                            `FAILED at ${i + 1}/${actions.length}\nassertSetting: ${action.description}`,
                            '#ff4444'
                        )
                        return result
                    }
                    break
                }
            }

            completedActions++
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : String(err)
            console.error(`[TestRunner] FAILED at action ${i + 1}: ${actionDesc}`)
            console.error(`[TestRunner] Error: ${errorMsg}`)

            // Log game state for debugging
            logGameState()
            stopCapturingLogs()

            const recentLogs = getRecentLogs(30)
            updateStatus(
                `FAILED at ${i + 1}/${actions.length}\n${actionDesc}\n${errorMsg}\n\n--- Recent Logs ---\n${recentLogs}`,
                '#ff4444'
            )

            return {
                passed: false,
                totalActions: actions.length,
                completedActions,
                failedAction: { index: i, action, error: errorMsg },
                duration: Date.now() - startTime,
            }
        }
    }

    const duration = Date.now() - startTime
    stopCapturingLogs()
    console.log(`\n[TestRunner] ═══════════════════════════════════════════`)
    console.log(`[TestRunner] PASSED: ${testName}`)
    console.log(
        `[TestRunner] ${completedActions}/${actions.length} actions in ${(duration / 1000).toFixed(1)}s`
    )
    console.log(`[TestRunner] ═══════════════════════════════════════════\n`)

    updateStatus(
        `PASSED: ${testName}\n${completedActions}/${actions.length} actions in ${(duration / 1000).toFixed(1)}s`,
        '#00ff88'
    )

    // Remove overlay after 60 seconds on success (long enough for screenshots)
    setTimeout(removeStatusOverlay, 60000)

    return {
        passed: true,
        totalActions: actions.length,
        completedActions,
        duration,
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

function formatAction(action: TestAction): string {
    switch (action.type) {
        case 'tapStock':
            return 'tapStock'
        case 'drag':
            return `drag ${action.cardId} → ${action.target}`
        case 'doubleTap':
            return `doubleTap ${action.cardId}`
        case 'tapUndo':
            return 'tapUndo'
        case 'tapAutoComplete':
            return 'tapAutoComplete'
        case 'assert':
            return `assert: ${action.description}`
        case 'waitForWin':
            return 'waitForWin'
        case 'seedWinState':
            return 'seedWinState'
        case 'tapMenuButton':
            return 'tapMenuButton'
        case 'tapNewGameButton':
            return 'tapNewGameButton'
        case 'tapMenuHitArea':
            return `tapMenuHitArea: ${action.hitAreaId}`
        case 'assertAppState':
            return `assertAppState: ${action.expected} — ${action.description}`
        case 'assertSetting':
            return `assertSetting: ${action.key}=${JSON.stringify(action.expected)} — ${action.description}`
    }
}

function logGameState(): void {
    const state = getCurrentCardState()
    console.log('[TestRunner] Current game state:')
    console.log(`  Stock: ${state.stock.length} cards`)
    console.log(`  Waste: ${state.waste.map((c) => c.value + c.type).join(', ') || '(empty)'}`)
    for (let i = 0; i < state.tableau.length; i++) {
        const pile = state.tableau[i]
        const display = pile.map((c) => (c.isFaceDown ? `[${c.value}${c.type}]` : c.value + c.type)).join(' ')
        console.log(`  Tableau ${i}: ${display || '(empty)'}`)
    }
    for (let i = 0; i < state.foundation.length; i++) {
        const pile = state.foundation[i]
        const top = pile.length > 0 ? pile[pile.length - 1].value + pile[pile.length - 1].type : '(empty)'
        console.log(`  Foundation ${i}: ${pile.length} cards, top: ${top}`)
    }
}
